import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import { API_BASE_URL } from '../api';
import './TestCasePage.css';

const EMPTY_FORM = {
  name: '',
  description: '',
  preconditions: '',
  expectedResult: '',
  priority: 'Medium',
  category: '',
};

/**
 * Create or edit a test case (with its ordered steps).
 *   /projects/:projectId/suites/:suiteId/cases/new
 *   /projects/:projectId/suites/:suiteId/cases/:caseId/edit
 */
function TestCaseFormPage() {
  const { projectId, suiteId, caseId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(caseId);
  const token = localStorage.getItem('token');

  const [project, setProject] = useState(null);
  const [suite, setSuite] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [stepsList, setStepsList] = useState([]);
  const [currentStep, setCurrentStep] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const savingRef = useRef(false);

  const suitePath = `/projects/${projectId}/suites/${suiteId}`;

  useEffect(() => {
    let cancelled = false;
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    fetch(`${API_BASE_URL}/projects/${projectId}`, auth)
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => { if (!cancelled && p) setProject(p); })
      .catch(() => {});

    fetch(`${API_BASE_URL}/suites/${suiteId}`, auth)
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => { if (!cancelled && s) setSuite(s); })
      .catch(() => {});

    if (isEdit) {
      fetch(`${API_BASE_URL}/testcases/${caseId}`, auth)
        .then((r) => (r.ok ? r.json() : null))
        .then((tc) => {
          if (cancelled) return;
          if (!tc) {
            setError('This test case could not be found.');
            return;
          }
          setFormData({
            name: tc.name || '',
            description: tc.description || '',
            preconditions: tc.preconditions || '',
            expectedResult: tc.expected_result || '',
            priority: tc.priority || 'Medium',
            category: tc.category || '',
          });
          setStepsList(tc.steps ? tc.steps.map((s) => s.description) : []);
        })
        .catch(() => { if (!cancelled) setError('Failed to load the test case.'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }

    return () => { cancelled = true; };
  }, [projectId, suiteId, caseId, isEdit, token]);

  const handleAddStep = () => {
    if (currentStep.trim()) {
      setStepsList([...stepsList, currentStep.trim()]);
      setCurrentStep('');
    }
  };

  const handleStepKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddStep();
    }
  };

  const removeStep = (index) => setStepsList(stepsList.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      const method = isEdit ? 'PUT' : 'POST';
      const url = isEdit
        ? `${API_BASE_URL}/testcases/${caseId}`
        : `${API_BASE_URL}/testcases`;

      const response = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          testSuiteId: parseInt(suiteId, 10),
          ...formData,
          steps: stepsList,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        navigate(isEdit ? `${suitePath}/cases/${caseId}` : suitePath);
      } else {
        setError(data.error || 'Failed to save test case.');
        savingRef.current = false;
        setSaving(false);
      }
    } catch (err) {
      console.error('Error saving test case:', err);
      setError('Error saving test case: ' + err.message);
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="test-case-page"><div className="container"><p className="loading">Loading...</p></div></div>;
  }

  return (
    <div className="test-case-page">
      <div className="container">
        <Breadcrumb
          items={[
            { label: 'Projects', to: '/projects' },
            { label: project?.name || `Project ${projectId}`, to: `/projects/${projectId}` },
            { label: suite?.name || `Suite ${suiteId}`, to: suitePath },
            { label: isEdit ? 'Edit test case' : 'New test case' },
          ]}
        />
        <h1>{isEdit ? 'Edit Test Case' : 'Create New Test Case'}</h1>

        {error && <div className="error-banner">{error}</div>}

        <div className="card form-card">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Test Case Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="priority">Priority</label>
                <select
                  id="priority"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                >
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <input
                  id="category"
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="preconditions">Preconditions</label>
              <textarea
                id="preconditions"
                value={formData.preconditions}
                onChange={(e) => setFormData({ ...formData, preconditions: e.target.value })}
                rows="2"
              />
            </div>

            <div className="form-group">
              <label>Test Steps (Press Enter to add)</label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <input
                  type="text"
                  value={currentStep}
                  onChange={(e) => setCurrentStep(e.target.value)}
                  onKeyPress={handleStepKeyPress}
                  placeholder="Enter a test step..."
                  style={{ flex: 1 }}
                />
                <button type="button" className="btn btn-primary" onClick={handleAddStep} style={{ padding: '8px 16px' }}>
                  Add
                </button>
              </div>

              {stepsList.length > 0 && (
                <div style={{ background: '#f9f9f9', padding: '15px 20px', borderRadius: '4px', marginBottom: '10px' }}>
                  {stepsList.map((step, index) => (
                    <div key={index} style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                      <span style={{ flex: 1, fontWeight: '500' }}>
                        <strong>{index + 1}.</strong> {step}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeStep(index)}
                        style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', padding: '4px 8px', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="expectedResult">Expected Result</label>
              <textarea
                id="expectedResult"
                value={formData.expectedResult}
                onChange={(e) => setFormData({ ...formData, expectedResult: e.target.value })}
                rows="3"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : isEdit ? 'Update Test Case' : 'Create Test Case'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate(isEdit ? `${suitePath}/cases/${caseId}` : suitePath)}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default TestCaseFormPage;
